import React, { RefObject } from 'react';
import styled from 'styled-components';
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// 컨테이너를 수평 정렬로 변경
const DownloadButtonWrapper = styled.button`
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #2282e9;
  background: none;
  border: none;
  padding: 8px 12px;
  outline: none;
  position: relative; // 상대 위치 설정
  
  &:hover {
    text-decoration: underline;
  }
`;

// 아이콘을 위한 새로운 스타일 컴포넌트
const IconWrapper = styled.span`
  position: absolute;
  top: -10px;
  right: 10px; // 오른쪽 정렬
  transform: translate(-5px, -30%); // 'E' 위로 이동하기 위한 값 조정
`;

interface InvoiceDownloadProps {
  onClick?: () => void;
  contentBoxRef?: RefObject<HTMLDivElement>;
}

const InvoiceDownload: React.FC<InvoiceDownloadProps> = ({ onClick, contentBoxRef }) => {
  const handleDownload = async () => {
    try {
      // ContentBox 요소 찾기
      const contentBox = contentBoxRef?.current || document.querySelector('.content-box') as HTMLElement;
      
      if (!contentBox) {
        console.error('ContentBox 요소를 찾을 수 없습니다.');
        return;
      }

      // PDF 생성 전에 원본 요소의 스타일과 내용을 저장
      const originalStyles: string[] = [];
      const originalContents: string[] = [];
      
      // 1. 더 구체적으로 GrayBoxImg 요소 찾기 (여러 선택자 시도)
      let grayBoxImgElements: HTMLElement[] = [];
      
      // 직접 스타일 속성으로 찾기 (background-image가 있는 모든 요소)
      const allElements = contentBox.querySelectorAll<HTMLElement>('*');
      allElements.forEach(el => {
        const style = window.getComputedStyle(el);
        if (style.backgroundImage && style.backgroundImage !== 'none') {
          const urlMatch = /url\(['"]?(.*?)['"]?\)/.exec(style.backgroundImage);
          if (urlMatch && urlMatch[1]) {
            grayBoxImgElements.push(el);
          }
        }
      });
      
      console.log(`배경 이미지가 있는 요소 수: ${grayBoxImgElements.length}`);
      
      // 2. 모든 배경 이미지 요소 처리
      for (let i = 0; i < grayBoxImgElements.length; i++) {
        const element = grayBoxImgElements[i];
        
        // 원본 스타일과 내용 저장
        originalStyles[i] = element.getAttribute('style') || '';
        originalContents[i] = element.innerHTML;
        
        // 3. 배경 이미지 URL 추출
        const computedStyle = window.getComputedStyle(element);
        const backgroundImage = computedStyle.backgroundImage;
        console.log(`요소 ${i+1}의 배경 이미지:`, backgroundImage);
        
        if (backgroundImage && backgroundImage !== 'none') {
          // 4. URL 추출 (url("...") 형식에서 URL만 추출)
          const urlMatch = /url\(['"]?(.*?)['"]?\)/.exec(backgroundImage);
          if (urlMatch && urlMatch[1]) {
            const imageUrl = urlMatch[1];
            console.log(`요소 ${i+1}의 이미지 URL:`, imageUrl);
            
            try {
              // 5. 이미지 캐시 방지를 위한 타임스탬프 추가
              const cacheBusterUrl = imageUrl.includes('?') 
                ? `${imageUrl}&t=${new Date().getTime()}` 
                : `${imageUrl}?t=${new Date().getTime()}`;
                
              // 6. 이미지 요소 생성
              const imgElement = document.createElement('img');
              imgElement.crossOrigin = 'anonymous'; // CORS 설정 먼저!
              imgElement.src = cacheBusterUrl;
              
              // 7. 이미지 스타일 설정
              imgElement.style.width = '100%';
              imgElement.style.height = '100%';
              imgElement.style.objectFit = 'cover';
              imgElement.style.display = 'block';
              
              if (computedStyle.borderRadius) {
                imgElement.style.borderRadius = computedStyle.borderRadius;
              }
              
              // 8. 원래 요소의 배경 이미지 제거하고 이미지 추가
              element.style.backgroundImage = 'none'; 
              element.innerHTML = '';
              element.appendChild(imgElement);
              
              // 9. 이미지 로드 완료 대기
              await new Promise<void>((resolve, reject) => {
                const timeout = setTimeout(() => {
                  console.log(`요소 ${i+1}의 이미지 로드 타임아웃:`, imageUrl);
                  resolve();
                }, 5000); // 5초 타임아웃
                
                imgElement.onload = () => {
                  console.log(`요소 ${i+1}의 이미지 로드 성공:`, imageUrl);
                  clearTimeout(timeout);
                  resolve();
                };
                
                imgElement.onerror = (e) => {
                  console.error(`요소 ${i+1}의 이미지 로드 실패:`, imageUrl, e);
                  clearTimeout(timeout);
                  resolve(); // 에러가 발생해도 진행
                };
                
                // 이미 로드된 경우
                if (imgElement.complete) {
                  console.log(`요소 ${i+1}의 이미지 이미 로드됨:`, imageUrl);
                  clearTimeout(timeout);
                  resolve();
                }
              });
            } catch (error) {
              console.error(`요소 ${i+1} 처리 중 오류:`, error);
            }
          }
        }
      }
      
      // 10. DOM 업데이트가 완료되도록 지연
      console.log('이미지 처리 완료, PDF 생성 준비 중...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 11. 현재 시간으로 파일명 생성
      const date = new Date();
      const fileName = `플리더스_테스트견적서_${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}.pdf`;
      
      // 12. PDF 생성 (개선된 설정)
      console.log('PDF 캡처 시작...');
      const canvas = await html2canvas(contentBox, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        imageTimeout: 10000, // 이미지 로딩 대기 시간 증가
        logging: true,
        onclone: (document, clonedDoc) => {
          // 클론된 문서의 이미지에 crossOrigin 속성 추가
          const images = clonedDoc.querySelectorAll('img');
          images.forEach(img => {
            img.crossOrigin = 'anonymous';
          });
          return clonedDoc;
        }
      });
      
      console.log('캔버스 생성 완료, PDF 변환 시작...');
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(fileName);
      console.log('PDF 저장 완료!');
      
      // 13. 원래 상태로 복원 (선택사항)
      for (let i = 0; i < grayBoxImgElements.length; i++) {
        const element = grayBoxImgElements[i];
        if (originalStyles[i]) {
          element.setAttribute('style', originalStyles[i]);
        }
        if (originalContents[i]) {
          element.innerHTML = originalContents[i];
        }
      }
      
    } catch (error) {
      console.error('PDF 생성 중 오류 발생:', error);
    }
    
    if (onClick) onClick();
  };

  return (
    <DownloadButtonWrapper type="button" onClick={handleDownload}>
      INVOICE
      <IconWrapper>
        <FontAwesomeIcon 
          icon={faDownload} 
          style={{
            color: "#797979",
            width: "14px",
            height: "14px"
          }}
        />
      </IconWrapper>
    </DownloadButtonWrapper>
  );
};

export default InvoiceDownload; 